import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';

import { NumeronCollectionModel } from '../models/numeron.model';
import { NumeronsService } from '../services/numerons.service';
import { Ga4AnalyticsService } from '../../../core/analytics/ga4-analytics.service';
import { InfiniteScrollSentinelDirective } from '../../../shared/directives/infinite-scroll-sentinel.directive';
import { OrderedMasonryDirective } from '../../../shared/directives/ordered-masonry.directive';
import { OrderedMasonryItemDirective } from '../../../shared/directives/ordered-masonry-item.directive';
import { CollectionFeedCardComponent } from '../../../shared/ui/collection-feed-card/collection-feed-card.component';
import { NumeronCardComponent } from '../../../shared/ui/numeron-card/numeron-card.component';

@Component({
  selector: 'app-most-viewed-page',
  imports: [
    InfiniteScrollSentinelDirective,
    OrderedMasonryDirective,
    OrderedMasonryItemDirective,
    CollectionFeedCardComponent,
    NumeronCardComponent,
  ],
  templateUrl: './most-viewed-page.component.html',
})
export class MostViewedPageComponent {
  private readonly numeronsService = inject(NumeronsService);
  private readonly ga4AnalyticsService = inject(Ga4AnalyticsService);
  private readonly title = inject(Title);
  private readonly destroyRef = inject(DestroyRef);

  private readonly pageSize = 10;
  private offset = 0;

  protected readonly collections = signal<NumeronCollectionModel[]>([]);
  protected readonly isLoading = signal(false);
  protected readonly isLoadingMore = signal(false);
  protected readonly hasError = signal(false);
  protected readonly hasMore = signal(true);

  public constructor() {
    this.title.setTitle('Most Viewed');
    this.ga4AnalyticsService.trackPageView({
      contentGroup: 'Most Viewed',
      contentType: 'Collections',
    });
    this.fetchMostViewed(true);
  }

  protected trackByCollectionId(_: number, collection: NumeronCollectionModel): string {
    return collection._id;
  }

  protected loadMore(): void {
    this.fetchMostViewed(false);
  }

  private fetchMostViewed(isInitialLoad: boolean): void {
    if (!this.hasMore() || this.isLoading() || this.isLoadingMore()) {
      return;
    }

    this.hasError.set(false);

    if (isInitialLoad) {
      this.isLoading.set(true);
    } else {
      this.isLoadingMore.set(true);
    }

    this.numeronsService
      .getMostViewedNumeronCollections(this.offset, this.pageSize)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (incomingCollections) => {
          this.collections.update((existingCollections) =>
            isInitialLoad ? incomingCollections : [...existingCollections, ...incomingCollections],
          );
          this.offset += incomingCollections.length;
          this.hasMore.set(incomingCollections.length === this.pageSize);
          this.isLoading.set(false);
          this.isLoadingMore.set(false);
        },
        error: () => {
          this.hasError.set(true);
          this.isLoading.set(false);
          this.isLoadingMore.set(false);
        },
      });
  }
}
